/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useCallback } from 'preact/hooks';
import { Keys, containsKey, addKey, removeKey } from '../utils/UNSAFE_keys';
import { BaseSelector } from '../PRIVATE_BaseSelector';
import type { BaseSelectorProps as Props } from './BaseSelector.types';
import { SelectionDetail } from '../UNSAFE_Collection';

/**
 * Props for the Selector Component
 */
export type SelectorProps<K> = Pick<Props, 'aria-label' | 'aria-labelledby' | 'testId'> & {
  /**
   * Callback function to invoke when the selected keys has changed when
   * the checkbox is toggled.
   */
  onChange?: (detail: SelectionDetail<K>) => void;
  /**
   * The key associated with the Selector.
   */
  rowKey: K;
  /**
   * The selected keys.
   */
  selectedKeys: Keys<K>;
  /**
   * Determine whether to show the visual partial icon when selector is checked. If the
   * selector is checked and isPartial is set to true, the selector would show a partially
   * checked icon.
   */
  isPartial?: boolean;
  /**
   * Specifies the selection mode. For selection mode 'all', please refer to SelectorAll component.
   */
  selectionMode?: 'multiple' | 'single';
};

/**
 * The Selector component renders checkboxes in collections to support selection.
 */
export function Selector<K>({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  selectionMode = 'multiple',
  testId,
  onChange,
  rowKey,
  selectedKeys,
  isPartial
}: SelectorProps<K>) {
  const onSelectionHandler = useCallback(
    (event: KeyboardEvent | MouseEvent) => {
      const { key } = event as KeyboardEvent;
      if (key !== undefined && key !== ' ') {
        return;
      }

      handleSelect(event, rowKey, selectedKeys, selectionMode, onChange);
    },
    [rowKey, selectedKeys, selectionMode, onChange]
  );

  const isChecked = isPartial
    ? 'partial'
    : containsKey(selectedKeys, rowKey)
    ? 'checked'
    : 'unchecked';

  return (
    <BaseSelector
      testId={testId}
      onSelectionHandler={onSelectionHandler}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      isChecked={isChecked}
    />
  );
}

const handleSelect = <K,>(
  event: MouseEvent | KeyboardEvent,
  rowKey: K,
  selectedKeys: SelectorProps<K>['selectedKeys'],
  selectionMode: SelectorProps<K>['selectionMode'],
  onChange?: SelectorProps<K>['onChange']
) => {
  if (onChange) {
    const newKeys = getNewKeys(rowKey, selectedKeys, selectionMode);
    onChange({ value: newKeys, target: event.target });

    // we don't want container component like ListView to process the click event,
    // and we also stop the event propagation in keyup due to issue in JET-60156
    event.stopPropagation();
  }
};

const getNewKeys = <K,>(
  rowKey: K,
  selectedKeys: SelectorProps<K>['selectedKeys'],
  selectionMode: SelectorProps<K>['selectionMode']
) => {
  let newKeys;
  const checked = containsKey(selectedKeys, rowKey);
  if (checked) {
    newKeys = removeKey(selectedKeys, rowKey, false);
  } else {
    if (selectionMode === 'single') {
      newKeys = { all: false, keys: new Set([rowKey]) } as Keys<K>;
    } else {
      newKeys = addKey(selectedKeys, rowKey);
    }
  }
  return newKeys;
};
